/**
 * Account detail page (PRD §11.4 + §10.4 + §9.3).
 *
 * One page does four things:
 *   - Render the account fields and let Manager+ edit name / is_public / owner.
 *   - List the explicit per-user grants (the AccountAccess rows that make
 *     §7.2 rule (3) fire) and let Manager+ add / revoke.
 *   - Show the visible contacts under this account, deep-linked.
 *   - Admin-only Danger zone for soft-delete (re-auth gated server-side).
 *
 * Field-level role gates are enforced server-side; the UI mirrors them so
 * users don't see buttons they can't use.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, createFileRoute, useNavigate, useSearch } from "@tanstack/react-router";
import {
  Briefcase,
  ChevronDown,
  ChevronRight,
  Loader2,
  Pencil,
  Plus,
  Save,
  Trash2,
  UserMinus,
  UserPlus,
} from "lucide-react";

import { type FormEvent, useEffect, useMemo, useState } from "react";

import { BackLink } from "../components/BackLink";

import { PipelineStrip } from "../components/accounts/PipelineStrip";
import { ChatterPanel } from "../components/chatter/ChatterPanel";
import { CustomFieldsPanel } from "../components/custom-fields/CustomFieldsPanel";
import { DealStagePill, DealStatusPill, DealValueChip } from "../components/deals/Pills";
import { DispatchesPanel } from "../components/erp/DispatchesPanel";
import { FollowUpsPanel } from "../components/followups/FollowUpsPanel";
import { TagChips } from "../components/tags/TagChips";
import {
  type Account,
  type AccountUpdate,
  ApiError,
  type Deal,
  type UserListItem,
  accountsApi,
  contactsApi,
  dealsApi,
  usersApi,
} from "../lib/api";
import { useAuthStore } from "../lib/auth";

interface AccountDetailSearch {
  /** Optional ``?note=<id>`` deep-link from the bell / email — handed
   * straight to the chatter panel which scrolls + highlights it. */
  note?: string;
}

export const Route = createFileRoute("/_app/accounts/$accountId")({
  validateSearch: (search: Record<string, unknown>): AccountDetailSearch => {
    const n = search.note;
    return typeof n === "string" ? { note: n } : {};
  },
  component: AccountDetailPage,
});

function AccountDetailPage() {
  const { accountId } = Route.useParams();
  const { note: highlightNoteId } = useSearch({ from: "/_app/accounts/$accountId" });
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const user = useAuthStore((s) => s.user);

  const [editing, setEditing] = useState(false);

  const {
    data: account,
    error,
    isLoading,
  } = useQuery({
    queryKey: ["account", accountId],
    queryFn: () => accountsApi.get(accountId),
  });

  if (isLoading) {
    return (
      <Shell>
        <p className="text-sm opacity-60">Loading…</p>
      </Shell>
    );
  }

  if (error) {
    const status = error instanceof ApiError ? error.status : 0;
    return (
      <Shell>
        <div
          role="alert"
          className="rounded-md border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-900 dark:bg-red-950/40 dark:text-red-300"
        >
          {status === 404
            ? "This account doesn't exist or you don't have access to it."
            : `Couldn't load account: ${(error as Error).message}`}
        </div>
      </Shell>
    );
  }

  // No data yet, but neither the loading nor error branch above matched —
  // this is react-query's "paused" state (offline / flaky network on the
  // field PWA). Show a placeholder instead of a blank screen.
  if (!account || !user) {
    return (
      <Shell>
        <p className="text-sm opacity-60">Loading…</p>
      </Shell>
    );
  }

  const isPrivileged = user.role === "admin" || user.role === "manager";
  const canEdit = isPrivileged;
  const canManageGrants = isPrivileged;
  const canDelete = user.role === "admin";

  return (
    <Shell title={account.name}>
      <PipelineStrip accountId={accountId} />

      {editing ? (
        <AccountEditor
          account={account}
          canEdit={canEdit}
          onSaved={(updated) => {
            queryClient.setQueryData(["account", accountId], updated);
            queryClient.invalidateQueries({ queryKey: ["accounts"] });
            setEditing(false);
          }}
          onCancel={() => setEditing(false)}
        />
      ) : (
        <AccountView account={account} canEdit={canEdit} onEdit={() => setEditing(true)} />
      )}

      {canManageGrants && (
        <GrantsCard
          accountId={accountId}
          canManage={canManageGrants}
          onChanged={() => {
            queryClient.invalidateQueries({ queryKey: ["account-grants", accountId] });
            queryClient.invalidateQueries({ queryKey: ["accounts"] });
          }}
        />
      )}

      <DealsForAccount account={account} />

      <LinkedContactsCard accountId={accountId} />

      <FollowUpsPanel
        parentType="account"
        parentId={account.id}
        defaultAssignedToId={account.owner_id ?? null}
      />

      <DispatchesPanel parentType="account" parentId={account.id} />

      <CustomFieldsPanel entity="accounts" recordKey={account.id} />

      <ChatterPanel
        parentType="account"
        parentId={account.id}
        highlightNoteId={highlightNoteId ?? null}
      />

      {canDelete && (
        <DeleteCard
          account={account}
          onDeleted={() => {
            queryClient.removeQueries({ queryKey: ["account", accountId] });
            queryClient.invalidateQueries({ queryKey: ["accounts"] });
            queryClient.invalidateQueries({ queryKey: ["accounts-trash"] });
            navigate({ to: "/accounts" });
          }}
        />
      )}
    </Shell>
  );
}

function Shell({ title, children }: { title?: string; children: React.ReactNode }) {
  return (
    <div className="mx-auto max-w-2xl space-y-6 p-4 sm:p-6">
      <header className="space-y-1">
        <BackLink fallbackTo="/accounts" fallbackLabel="accounts" />
        <h1 className="text-2xl font-semibold tracking-tight">{title || "Account"}</h1>
      </header>
      {children}
    </div>
  );
}

// ── Edit form (Manager+ only) ───────────────────────────────────────────────

interface EditorProps {
  account: Account;
  canEdit: boolean;
  onSaved: (a: Account) => void;
  onCancel: () => void;
}

function AccountEditor({ account, canEdit, onSaved, onCancel }: EditorProps) {
  const [name, setName] = useState(account.name);
  const [isPublic, setIsPublic] = useState(account.is_public);
  const [ownerId, setOwnerId] = useState(account.owner_id ?? "");
  const [address, setAddress] = useState(account.address ?? "");
  const [error, setError] = useState<string | null>(null);

  const { data: users } = useQuery({
    queryKey: ["users", { active_only: true }],
    queryFn: () => usersApi.list({ active_only: true }),
    enabled: canEdit,
  });

  useEffect(() => {
    setName(account.name);
    setIsPublic(account.is_public);
    setOwnerId(account.owner_id ?? "");
  }, [account]);

  const updateMutation = useMutation({
    mutationFn: (body: AccountUpdate) => accountsApi.update(account.id, body),
    onSuccess: (updated) => {
      onSaved(updated);
      setError(null);
    },
    onError: (err) => {
      const msg = err instanceof ApiError ? err.message : "Could not save changes.";
      setError(msg);
    },
  });

  const onSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError(null);

    const body: AccountUpdate = {};
    if (canEdit) {
      if (name !== account.name) body.name = name;
      if (isPublic !== account.is_public) body.is_public = isPublic;
      const ownerNorm = ownerId.trim() || null;
      if (ownerNorm !== account.owner_id) body.owner_id = ownerNorm;
      const addressNorm = address.trim() || null;
      if (addressNorm !== account.address) body.address = addressNorm;
    }

    if (Object.keys(body).length === 0) {
      setError("Nothing to save — no fields have changed.");
      return;
    }

    updateMutation.mutate(body);
  };

  const submitting = updateMutation.isPending;

  return (
    <form
      onSubmit={onSubmit}
      className="space-y-4 rounded-xl border border-[var(--border)] p-5"
      noValidate
    >
      <Field id="account-name" label="Name">
        <input
          id="account-name"
          type="text"
          value={name}
          disabled={!canEdit}
          onChange={(e) => setName(e.target.value)}
          className="w-full rounded-md border border-[var(--border)] bg-transparent px-3 py-2 text-sm outline-none focus:border-[var(--accent)] disabled:opacity-60"
        />
      </Field>

      <Field id="account-address" label="Address" hint="Optional. Default delivery address.">
        <textarea
          id="account-address"
          value={address}
          disabled={!canEdit}
          onChange={(e) => setAddress(e.target.value)}
          rows={2}
          maxLength={500}
          placeholder="Shipping / delivery address"
          className="w-full rounded-md border border-[var(--border)] bg-transparent px-3 py-2 text-sm outline-none focus:border-[var(--accent)] disabled:opacity-60"
        />
      </Field>

      <Field
        id="account-owner"
        label="Owner"
        hint={
          canEdit
            ? "Soft label only — owner gets no special access. Use grants below to give per-user access."
            : "Read-only — only managers and admins can change this."
        }
      >
        <select
          id="account-owner"
          value={ownerId}
          disabled={!canEdit}
          onChange={(e) => setOwnerId(e.target.value)}
          className="w-full rounded-md border border-[var(--border)] bg-transparent px-3 py-2 text-sm outline-none focus:border-[var(--accent)] disabled:opacity-60"
        >
          <option value="">(no owner)</option>
          {users?.items.map((u) => (
            <option key={u.id} value={u.id}>
              {u.name || u.email} — {u.role}
            </option>
          ))}
          {/* If the current owner isn't in the active-users list (e.g. they
              were disabled), still show a placeholder so the value renders. */}
          {ownerId && !users?.items.some((u) => u.id === ownerId) && (
            <option value={ownerId}>(unknown / inactive — {ownerId.slice(0, 8)}…)</option>
          )}
        </select>
      </Field>

      <label
        htmlFor="account-public"
        className={`flex items-center gap-2 rounded-md border border-[var(--border)] p-3 text-sm ${
          canEdit ? "cursor-pointer hover:bg-black/5 dark:hover:bg-white/5" : "opacity-60"
        }`}
      >
        <input
          id="account-public"
          type="checkbox"
          checked={isPublic}
          disabled={!canEdit}
          onChange={(e) => setIsPublic(e.target.checked)}
          className="size-4"
        />
        <span>
          <span className="font-medium">Public</span>
          <span className="block text-xs opacity-60">
            Visible to every Sales user without a grant. Use sparingly.
          </span>
        </span>
      </label>

      <Meta account={account} />

      {error && (
        <div
          role="alert"
          className="rounded-md border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-900 dark:bg-red-950/40 dark:text-red-300"
        >
          {error}
        </div>
      )}

      {canEdit ? (
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="submit"
            disabled={submitting}
            className="flex items-center gap-2 rounded-md bg-[var(--accent)] px-4 py-2 text-sm font-medium text-white transition-opacity hover:opacity-90 disabled:opacity-50"
          >
            {submitting ? (
              <>
                <Loader2 className="size-4 animate-spin" aria-hidden />
                Saving…
              </>
            ) : (
              <>
                <Save className="size-4" aria-hidden />
                Save changes
              </>
            )}
          </button>
          <button
            type="button"
            onClick={onCancel}
            disabled={submitting}
            className="rounded-md border border-[var(--border)] px-4 py-2 text-sm font-medium hover:bg-black/5 dark:hover:bg-white/5 disabled:opacity-50"
          >
            Cancel
          </button>
        </div>
      ) : (
        <p className="text-xs opacity-60">You don't have permission to edit this account.</p>
      )}
    </form>
  );
}

// ── View mode ───────────────────────────────────────────────────────────────

function AccountView({
  account,
  canEdit,
  onEdit,
}: {
  account: Account;
  canEdit: boolean;
  onEdit: () => void;
}) {
  // Resolve the owner name. Like the contact / deal view the query is
  // shared and TanStack-cached so flipping between view ↔ edit is cheap.
  const usersQ = useQuery({
    queryKey: ["users", { active_only: true }],
    queryFn: () => usersApi.list({ active_only: true }),
  });
  const owner = account.owner_id && usersQ.data?.items.find((u) => u.id === account.owner_id);

  return (
    <section className="space-y-5 rounded-xl border border-[var(--border)] p-5">
      <div>
        <h2 className="text-xl font-semibold">{account.name}</h2>
      </div>

      <div>
        <div className="mb-1.5 text-xs uppercase tracking-wide opacity-50">Tags</div>
        <TagChips parentType="account" parentId={account.id} />
      </div>

      <dl className="grid gap-x-6 gap-y-3 text-sm sm:grid-cols-2">
        <ViewRow
          label="Owner"
          value={
            owner ? (
              <span>
                {owner.name || owner.email}{" "}
                <span className="text-xs opacity-60">— {owner.role}</span>
              </span>
            ) : account.owner_id ? (
              <span className="font-mono text-xs">{account.owner_id.slice(0, 8)}…</span>
            ) : (
              <em className="opacity-50">(no owner)</em>
            )
          }
        />
        <ViewRow
          label="Visibility"
          value={
            account.is_public ? (
              <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-medium text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300">
                Public
              </span>
            ) : (
              <span className="rounded-full bg-zinc-100 px-2 py-0.5 text-xs font-medium text-zinc-700 dark:bg-zinc-800/60 dark:text-zinc-300">
                Private — grants required
              </span>
            )
          }
        />
      </dl>

      <Meta account={account} />

      {canEdit ? (
        <button
          type="button"
          onClick={onEdit}
          className="inline-flex items-center gap-2 rounded-md border border-[var(--border)] px-4 py-2 text-sm font-medium hover:bg-black/5 dark:hover:bg-white/5"
        >
          <Pencil className="size-4" aria-hidden />
          Edit
        </button>
      ) : (
        <p className="text-xs opacity-60">You don't have permission to edit this account.</p>
      )}
    </section>
  );
}

function ViewRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="min-w-0">
      <dt className="text-xs uppercase tracking-wide opacity-50">{label}</dt>
      <dd className="mt-0.5 truncate">{value}</dd>
    </div>
  );
}

// ── Grants list + add/revoke (Manager+ only) ────────────────────────────────

function GrantsCard({
  accountId,
  canManage,
  onChanged,
}: {
  accountId: string;
  canManage: boolean;
  onChanged: () => void;
}) {
  const queryClient = useQueryClient();
  const [pickedUserId, setPickedUserId] = useState("");
  const [error, setError] = useState<string | null>(null);

  const grantsQ = useQuery({
    queryKey: ["account-grants", accountId],
    queryFn: () => accountsApi.listGrants(accountId),
  });

  const usersQ = useQuery({
    queryKey: ["users", { active_only: true }],
    queryFn: () => usersApi.list({ active_only: true }),
    enabled: canManage,
  });

  const addMutation = useMutation({
    mutationFn: (uid: string) => accountsApi.addGrant(accountId, uid),
    onSuccess: () => {
      setPickedUserId("");
      setError(null);
      queryClient.invalidateQueries({ queryKey: ["account-grants", accountId] });
      onChanged();
    },
    onError: (err) => {
      const msg = err instanceof ApiError ? err.message : "Could not grant access.";
      setError(msg);
    },
  });

  const revokeMutation = useMutation({
    mutationFn: (uid: string) => accountsApi.removeGrant(accountId, uid),
    onSuccess: () => {
      setError(null);
      queryClient.invalidateQueries({ queryKey: ["account-grants", accountId] });
      onChanged();
    },
    onError: (err) => {
      const msg = err instanceof ApiError ? err.message : "Could not revoke access.";
      setError(msg);
    },
  });

  // Lookup: user id -> display label.
  const userById = useMemo(() => {
    const m = new Map<string, UserListItem>();
    for (const u of usersQ.data?.items ?? []) m.set(u.id, u);
    return m;
  }, [usersQ.data]);

  const grantedIds = new Set((grantsQ.data?.items ?? []).map((g) => g.user_id));
  const candidates = (usersQ.data?.items ?? []).filter((u) => !grantedIds.has(u.id));

  return (
    <div className="space-y-3 rounded-xl border border-[var(--border)] p-5">
      <div>
        <h2 className="text-sm font-semibold">Per-user access</h2>
        <p className="text-xs opacity-60">
          Sales users only see this account if (a) it's public, (b) they own it, or (c) they're
          granted explicitly here. Managers and admins always see everything.
        </p>
      </div>

      {grantsQ.isLoading ? (
        <p className="text-sm opacity-60">Loading…</p>
      ) : grantsQ.data && grantsQ.data.items.length > 0 ? (
        <ul className="divide-y divide-[var(--border)] rounded-md border border-[var(--border)]">
          {grantsQ.data.items.map((g) => {
            const u = userById.get(g.user_id);
            return (
              <li
                key={g.user_id}
                className="flex items-center justify-between gap-3 px-3 py-2 text-sm"
              >
                <div className="min-w-0">
                  <div className="truncate font-medium">
                    {u ? u.name || u.email : `${g.user_id.slice(0, 8)}…`}
                  </div>
                  <div className="truncate text-xs opacity-60">
                    Granted {new Date(g.granted_at).toLocaleDateString()}
                  </div>
                </div>
                {canManage && (
                  <button
                    type="button"
                    onClick={() => revokeMutation.mutate(g.user_id)}
                    disabled={revokeMutation.isPending}
                    className="inline-flex items-center gap-1 rounded-md border border-[var(--border)] px-2 py-1 text-xs hover:bg-black/5 dark:hover:bg-white/5 disabled:opacity-50"
                  >
                    <UserMinus className="size-3" aria-hidden />
                    Revoke
                  </button>
                )}
              </li>
            );
          })}
        </ul>
      ) : (
        <p className="text-xs opacity-60">No explicit grants yet.</p>
      )}

      {canManage && (
        <div className="flex items-end gap-2">
          <div className="flex-1">
            <label htmlFor="grant-user" className="mb-1 block text-xs opacity-60">
              Grant access to
            </label>
            <select
              id="grant-user"
              value={pickedUserId}
              onChange={(e) => setPickedUserId(e.target.value)}
              className="w-full rounded-md border border-[var(--border)] bg-transparent px-3 py-2 text-sm outline-none focus:border-[var(--accent)]"
            >
              <option value="">Select a user…</option>
              {candidates.map((u) => (
                <option key={u.id} value={u.id}>
                  {u.name || u.email} — {u.role}
                </option>
              ))}
            </select>
          </div>
          <button
            type="button"
            disabled={!pickedUserId || addMutation.isPending}
            onClick={() => pickedUserId && addMutation.mutate(pickedUserId)}
            className="inline-flex items-center gap-1.5 rounded-md bg-[var(--accent)] px-3 py-2 text-sm font-medium text-white hover:opacity-90 disabled:opacity-50"
          >
            <UserPlus className="size-4" aria-hidden />
            Grant
          </button>
        </div>
      )}

      {error && (
        <div
          role="alert"
          className="rounded-md border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-900 dark:bg-red-950/40 dark:text-red-300"
        >
          {error}
        </div>
      )}
    </div>
  );
}

// ── Deals on this account ───────────────────────────────────────────────────

/**
 * Inline deals list for the account detail page.
 *
 * The PipelineStrip above shows aggregate counts; this section is the
 * operational view — the actual deals you can click into. Open deals
 * are always visible (the day-to-day work). Past deals (closed-won /
 * closed-lost) are hidden behind a toggle so the section doesn't get
 * dominated by historical wins/losses on a long-running account; the
 * past-deals query only fires when the toggle is opened.
 *
 * Visibility is server-enforced (Sales sees only the deals they're
 * assigned to / on a contact they own), so a Sales viewer of this
 * page may see fewer deals than a Manager would. That's intentional.
 */
function DealsForAccount({ account }: { account: Account }) {
  const me = useAuthStore((s) => s.user);
  const isPrivileged = me?.role === "admin" || me?.role === "manager";
  const [showPast, setShowPast] = useState(false);

  // Sales can't list users (403), so we only fetch for Manager+. Same
  // approach as the contact-detail deals card.
  const usersQ = useQuery({
    queryKey: ["users", { active_only: true }],
    queryFn: () => usersApi.list({ active_only: true }),
    enabled: isPrivileged,
  });
  const usersById = useMemo(
    () => new Map((usersQ.data?.items ?? []).map((u) => [u.id, u])),
    [usersQ.data],
  );

  const openQ = useQuery({
    queryKey: ["deals", { account_id: account.id, open_only: true, limit: 50 }],
    queryFn: () => dealsApi.list({ account_id: account.id, open_only: true, limit: 50 }),
  });

  // Lazy: only fires once the user opts in to seeing closed deals. This
  // keeps the default page-load light on accounts with long histories.
  const pastQ = useQuery({
    queryKey: ["deals", { account_id: account.id, open_only: false, limit: 50, past: true }],
    queryFn: () => dealsApi.list({ account_id: account.id, open_only: false, limit: 50 }),
    enabled: showPast,
  });
  // The "open_only=false" call returns *all* deals (open + closed). We
  // want past-only here, so filter client-side. The server doesn't have
  // a closed_only flag and adding one for one UI surface isn't worth
  // it — past deals are bounded by the limit anyway.
  const pastDeals = (pastQ.data?.items ?? []).filter((d) => d.closed_at != null);

  const renderRow = (deal: Deal) => {
    const assignee = deal.assigned_to_id ? (usersById.get(deal.assigned_to_id) ?? null) : null;
    const assigneeLabel = (() => {
      if (!deal.assigned_to_id) return "Unassigned";
      if (deal.assigned_to_id === me?.id) return "You";
      if (assignee) return assignee.name || assignee.email.split("@")[0] || assignee.email;
      return `${deal.assigned_to_id.slice(0, 8)}…`;
    })();
    return <AccountDealRow key={deal.id} deal={deal} assigneeLabel={assigneeLabel} />;
  };

  return (
    <section className="space-y-3 rounded-xl border border-[var(--border)] p-5">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Briefcase className="size-4 opacity-70" aria-hidden />
          <h2 className="text-sm font-semibold">Deals</h2>
          {openQ.data && (
            <span className="rounded-full bg-black/5 px-2 py-0.5 text-xs opacity-70 dark:bg-white/10">
              {openQ.data.total} open
            </span>
          )}
        </div>
        <Link
          to="/deals/new"
          search={{ account: account.id }}
          className="inline-flex items-center gap-1.5 rounded-md border border-[var(--border)] px-2.5 py-1.5 text-xs font-medium hover:bg-black/5 dark:hover:bg-white/5"
        >
          <Plus className="size-3.5" aria-hidden />
          New deal
        </Link>
      </div>

      {/* Open deals — always rendered. */}
      {openQ.isLoading ? (
        <p className="text-xs opacity-60">Loading…</p>
      ) : openQ.error ? (
        <p className="text-xs text-red-700 dark:text-red-300">
          Couldn't load deals: {(openQ.error as Error).message}
        </p>
      ) : !openQ.data || openQ.data.items.length === 0 ? (
        <p className="text-xs opacity-60">No open deals on this account.</p>
      ) : (
        <ul className="divide-y divide-[var(--border)] overflow-hidden rounded-md border border-[var(--border)]">
          {openQ.data.items.map(renderRow)}
        </ul>
      )}

      {/* Past deals — collapsible. */}
      <div className="border-t border-[var(--border)] pt-3">
        <button
          type="button"
          onClick={() => setShowPast((v) => !v)}
          className="inline-flex items-center gap-1.5 text-xs opacity-70 hover:opacity-100"
          aria-expanded={showPast}
        >
          {showPast ? (
            <ChevronDown className="size-3.5" aria-hidden />
          ) : (
            <ChevronRight className="size-3.5" aria-hidden />
          )}
          {showPast ? "Hide past deals" : "Show past deals"}
        </button>

        {showPast && (
          <div className="mt-3">
            {pastQ.isLoading ? (
              <p className="text-xs opacity-60">Loading past deals…</p>
            ) : pastQ.error ? (
              <p className="text-xs text-red-700 dark:text-red-300">
                Couldn't load past deals: {(pastQ.error as Error).message}
              </p>
            ) : pastDeals.length === 0 ? (
              <p className="text-xs opacity-60">No closed deals yet.</p>
            ) : (
              <ul className="divide-y divide-[var(--border)] overflow-hidden rounded-md border border-[var(--border)]">
                {pastDeals.map(renderRow)}
              </ul>
            )}
          </div>
        )}
      </div>
    </section>
  );
}

function AccountDealRow({ deal, assigneeLabel }: { deal: Deal; assigneeLabel: string }) {
  return (
    <li>
      <Link
        to="/deals/$dealId"
        params={{ dealId: deal.id }}
        className="flex flex-wrap items-center justify-between gap-3 px-3 py-2 text-sm transition-colors hover:bg-black/5 dark:hover:bg-white/5"
      >
        <div className="min-w-0 space-y-0.5">
          <div className="text-xs opacity-80">
            {deal.contact_summary ? (
              <>
                With <span className="font-medium opacity-100">{deal.contact_summary.name}</span>
                {" · "}
              </>
            ) : null}
            Assigned to <span className="font-medium opacity-100">{assigneeLabel}</span>
          </div>
          <div className="text-xs opacity-60">
            Created {new Date(deal.created_at).toLocaleDateString()}
            {deal.closed_at && (
              <>
                {" · Closed "}
                {new Date(deal.closed_at).toLocaleDateString()}
              </>
            )}
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-1.5">
          <DealStagePill stage={deal.stage} />
          <DealStatusPill status={deal.status} />
          <DealValueChip value={deal.value} />
        </div>
      </Link>
    </li>
  );
}

// ── Linked contacts (read-only deep-link list) ──────────────────────────────

function LinkedContactsCard({ accountId }: { accountId: string }) {
  const idsQ = useQuery({
    queryKey: ["account-contact-ids", accountId],
    queryFn: () => accountsApi.listContactIds(accountId),
  });

  return (
    <div className="space-y-3 rounded-xl border border-[var(--border)] p-5">
      <div>
        <h2 className="text-sm font-semibold">Contacts on this account</h2>
        <p className="text-xs opacity-60">
          Visibility-filtered: you see the contacts you have access to. To attach a contact, edit it
          and set its account.
        </p>
      </div>

      {idsQ.isLoading ? (
        <p className="text-sm opacity-60">Loading…</p>
      ) : idsQ.data && idsQ.data.length > 0 ? (
        <ul className="divide-y divide-[var(--border)] rounded-md border border-[var(--border)]">
          {idsQ.data.map((id) => (
            <ContactRow key={id} contactId={id} />
          ))}
        </ul>
      ) : (
        <p className="text-xs opacity-60">No contacts yet.</p>
      )}
    </div>
  );
}

function ContactRow({ contactId }: { contactId: string }) {
  // Each row hits its own GET — TanStack Query dedupes/caches across the
  // app, so subsequent renders (and the contact detail page itself) are
  // free. The N+1 cost here is bounded by the visible-contacts count
  // returned by the parent endpoint, intentionally small.
  const { data, error } = useQuery({
    queryKey: ["contact", contactId],
    queryFn: () => contactsApi.get(contactId),
  });

  if (error) {
    return (
      <li className="px-3 py-2 text-xs opacity-60">(couldn't load {contactId.slice(0, 8)}…)</li>
    );
  }
  if (!data) {
    return <li className="px-3 py-2 text-xs opacity-60">Loading…</li>;
  }

  return (
    <li>
      <Link
        to="/contacts/$contactId"
        params={{ contactId: data.id }}
        className="flex items-center justify-between gap-3 px-3 py-2 text-sm transition-colors hover:bg-black/5 dark:hover:bg-white/5"
      >
        <div className="min-w-0">
          <div className="truncate font-medium">{data.name}</div>
          {data.email && <div className="truncate text-xs opacity-60">{data.email}</div>}
        </div>
        <div className="font-mono text-xs opacity-80">{data.phone}</div>
      </Link>
    </li>
  );
}

// ── Soft-delete (Admin only) ────────────────────────────────────────────────

function DeleteCard({ account, onDeleted }: { account: Account; onDeleted: () => void }) {
  const [confirming, setConfirming] = useState(false);
  const [typedName, setTypedName] = useState("");
  const [error, setError] = useState<string | null>(null);

  const deleteMutation = useMutation({
    mutationFn: () => accountsApi.remove(account.id),
    onSuccess: () => onDeleted(),
    onError: (err) => {
      if (err instanceof ApiError && err.reauthRequired) {
        setError("For security, please log out and log back in to confirm this delete.");
        return;
      }
      const msg = err instanceof ApiError ? err.message : "Could not delete account.";
      setError(msg);
    },
  });

  return (
    <div className="space-y-3 rounded-xl border border-red-300/60 p-5 dark:border-red-900/60">
      <div className="flex items-start gap-3">
        <Trash2 className="mt-0.5 size-4 text-red-600 dark:text-red-400" aria-hidden />
        <div>
          <h2 className="text-sm font-semibold text-red-700 dark:text-red-300">Danger zone</h2>
          <p className="text-xs opacity-70">
            Soft-deleting hides this account from all lists. Contacts on it stay alive but lose this
            visibility root — they fall back to assigned/created visibility (PRD §7.4) until the
            account is restored from <em>Trash</em>.
          </p>
        </div>
      </div>

      {error && (
        <div
          role="alert"
          className="rounded-md border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-900 dark:bg-red-950/40 dark:text-red-300"
        >
          {error}
        </div>
      )}

      {!confirming ? (
        <button
          type="button"
          onClick={() => {
            setError(null);
            setConfirming(true);
          }}
          className="rounded-md border border-red-300 px-3 py-2 text-sm font-medium text-red-700 hover:bg-red-50 dark:border-red-900 dark:text-red-300 dark:hover:bg-red-950/40"
        >
          Move to trash…
        </button>
      ) : (
        <div className="space-y-2">
          <label htmlFor="confirm-name" className="block text-xs opacity-70">
            Type <strong>{account.name}</strong> to confirm:
          </label>
          <input
            id="confirm-name"
            type="text"
            value={typedName}
            onChange={(e) => setTypedName(e.target.value)}
            className="w-full rounded-md border border-[var(--border)] bg-transparent px-3 py-2 text-sm outline-none focus:border-red-500"
            autoComplete="off"
          />
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              disabled={deleteMutation.isPending || typedName !== account.name}
              onClick={() => deleteMutation.mutate()}
              className="rounded-md bg-red-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-50"
            >
              {deleteMutation.isPending ? "Deleting…" : "Confirm delete"}
            </button>
            <button
              type="button"
              disabled={deleteMutation.isPending}
              onClick={() => {
                setConfirming(false);
                setTypedName("");
              }}
              className="rounded-md border border-[var(--border)] px-3 py-1.5 text-sm hover:bg-black/5 dark:hover:bg-white/5"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Small bits ──────────────────────────────────────────────────────────────

function Meta({ account }: { account: Account }) {
  return (
    <dl className="grid grid-cols-2 gap-x-4 gap-y-1 border-t border-[var(--border)] pt-3 text-xs opacity-60">
      <dt>Created</dt>
      <dd>{new Date(account.created_at).toLocaleString()}</dd>
      <dt>Updated</dt>
      <dd>{new Date(account.updated_at).toLocaleString()}</dd>
    </dl>
  );
}

function Field({
  id,
  label,
  hint,
  children,
}: {
  id: string;
  label: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="text-sm">
      <label htmlFor={id} className="mb-1 block opacity-80">
        {label}
      </label>
      {children}
      {hint && <span className="mt-1 block text-xs opacity-50">{hint}</span>}
    </div>
  );
}
